import type { ComponentPropsWithoutRef } from "react"
import "./RoomStatusAndFPS.scss"
import { t } from "@lingui/core/macro"
import { BroadcastStatus, StreamStatus } from "@multimediallc/web-utils"
import {
    selectStreamFPS,
    selectStreamStatus,
} from "../../../store/broadcastSlice"
import { useAppSelector } from "../../../store/hooks"
import { Typography } from "../../common"
import { LineSeparator, RedDot } from "../../common/atoms/Icons/Broadcast"
import { useMobileBroadcastContext } from "../mobileBroadcastContext"

type RoomStatusAndFPSProps = ComponentPropsWithoutRef<"div">

export function RoomStatusAndFPS({ ...props }: RoomStatusAndFPSProps) {
    const i18n = {
        live: t`LIVE`,
        offline: t`OFFLINE`,
        private: t`Private`,
        public: t`Public`,
        hidden: t`Hidden`,
        away: t`Away`,
        password: t`Password Protected`,
    }

    const { broadcastStatus } = useMobileBroadcastContext()
    const fps = useAppSelector(selectStreamFPS)
    const streamStatus = useAppSelector(selectStreamStatus)

    const isOffline = [StreamStatus.Offline, StreamStatus.Unknown].includes(
        streamStatus,
    )

    let statusMessage = ""
    switch (streamStatus) {
        case StreamStatus.Public:
            statusMessage = i18n.public
            break
        case StreamStatus.Private:
            statusMessage = i18n.private
            break
        case StreamStatus.Hidden:
            statusMessage = i18n.hidden
            break
        case StreamStatus.Away:
            statusMessage = i18n.away
            break
        case StreamStatus.PasswordProtected:
            statusMessage = i18n.password
            break
        default:
            statusMessage = i18n.public
            break
    }

    if (broadcastStatus !== BroadcastStatus.Streaming || isOffline) {
        return (
            <div className="RoomStatusAndFPS" {...props}>
                <Typography
                    size="smpx"
                    className="RoomStatusAndFPS__dataItem"
                    data-testid="offline-status"
                >
                    {i18n.offline}
                </Typography>
            </div>
        )
    }

    return (
        <div className="RoomStatusAndFPS" {...props}>
            <RedDot className="RoomStatusAndFPS__redDot" />
            <Typography
                size="smpx"
                className="RoomStatusAndFPS__dataItem"
                data-testid="live-status"
            >
                {i18n.live}
            </Typography>
            <LineSeparator className="RoomStatusAndFPS__lineSeparator" />
            <Typography
                size="smpx"
                className="RoomStatusAndFPS__dataItem"
                data-testid="status-message"
            >
                {statusMessage}
            </Typography>
            <LineSeparator className="RoomStatusAndFPS__lineSeparator" />
            <Typography
                size="smpx"
                className="RoomStatusAndFPS__dataItem RoomStatusAndFPS__lastItem"
                data-testid="fps-status"
            >
                {Math.round(fps)} FPS
            </Typography>
        </div>
    )
}
